// Hub LAN discovery service — finds hubs on local network and provides local API fallback
import { saveHubAddress, getAllHubAddresses } from './offlineDb'

const HUB_LOCAL_PORT = 80
const DISCOVERY_TIMEOUT = 3000
const HEALTH_CHECK_INTERVAL = 30000

class HubDiscoveryService {
    constructor() {
        this.knownHubs = new Map() // hubId -> { ip, port, lastSeen, online }
        this.healthCheckTimer = null
        this.listeners = new Set()
    }

    // Try to reach a hub at a given IP via its local HTTP API
    async probeHub(ip, port = HUB_LOCAL_PORT) {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT)
        try {
            const res = await fetch(`http://${ip}:${port}/status`, {
                signal: controller.signal,
                mode: 'cors',
                headers: { 'Accept': 'application/json' },
            })
            clearTimeout(timeout)
            if (res.ok) {
                const data = await res.json()
                return { ip, port, online: true, hubName: data.hubName, hubId: data.hubId || data.mac, ...data }
            }
        } catch {
            clearTimeout(timeout)
        }
        return null
    }

    // Load cached hub addresses from IndexedDB and probe each
    async discoverFromCache() {
        try {
            const cached = await getAllHubAddresses()
            const results = await Promise.allSettled(
                cached.map(entry => this.probeHub(entry.ip, entry.port))
            )
            for (const result of results) {
                if (result.status === 'fulfilled' && result.value) {
                    const hub = result.value
                    this.knownHubs.set(hub.hubId, {
                        ip: hub.ip,
                        port: hub.port,
                        online: true,
                        lastSeen: Date.now(),
                        hubName: hub.hubName,
                    })
                    await saveHubAddress(hub.hubId, hub.ip, hub.port)
                }
            }
        } catch (err) {
            console.warn('[HubDiscovery] Cache probe failed:', err)
        }
        this._notifyListeners()
        return this.getOnlineHubs()
    }

    // Register a hub's LAN address (called when cloud server tells us hub IP, or from mDNS)
    async registerHub(hubId, ip, port = HUB_LOCAL_PORT) {
        const probe = await this.probeHub(ip, port)
        const online = !!probe
        this.knownHubs.set(hubId, {
            ip, port, online,
            lastSeen: online ? Date.now() : null,
            hubName: probe?.hubName || hubId,
        })
        await saveHubAddress(hubId, ip, port)
        this._notifyListeners()
        return online
    }

    // Send a command to a hub over LAN
    async sendLocalCommand(hubId, command) {
        const hub = this.knownHubs.get(hubId)
        if (!hub || !hub.online) {
            throw new Error(`Hub ${hubId} not reachable on LAN`)
        }
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 5000)
        try {
            const res = await fetch(`http://${hub.ip}:${hub.port}/control`, {
                method: 'POST',
                signal: controller.signal,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(command),
            })
            clearTimeout(timeout)
            if (!res.ok) throw new Error(`Hub returned ${res.status}`)
            return await res.json()
        } catch (err) {
            clearTimeout(timeout)
            hub.online = false
            this._notifyListeners()
            throw err
        }
    }

    // Get status from a hub over LAN
    async getLocalStatus(hubId) {
        const hub = this.knownHubs.get(hubId)
        if (!hub) return null
        const probe = await this.probeHub(hub.ip, hub.port)
        if (probe) {
            hub.online = true
            hub.lastSeen = Date.now()
        } else {
            hub.online = false
        }
        this._notifyListeners()
        return probe
    }

    // Get all hubs that are reachable on LAN
    getOnlineHubs() {
        const online = []
        for (const [hubId, info] of this.knownHubs) {
            if (info.online) online.push({ hubId, ...info })
        }
        return online
    }

    // Check if any hub is reachable on LAN
    hasLocalHub() {
        for (const info of this.knownHubs.values()) {
            if (info.online) return true
        }
        return false
    }

    // Start periodic health checks
    startHealthChecks() {
        if (this.healthCheckTimer) return
        this.healthCheckTimer = setInterval(() => this.discoverFromCache(), HEALTH_CHECK_INTERVAL)
    }

    stopHealthChecks() {
        if (this.healthCheckTimer) {
            clearInterval(this.healthCheckTimer)
            this.healthCheckTimer = null
        }
    }

    // Listener pattern for UI updates
    onChange(callback) {
        this.listeners.add(callback)
        return () => this.listeners.delete(callback)
    }

    _notifyListeners() {
        const state = { hubs: this.getOnlineHubs(), hasLocal: this.hasLocalHub() }
        for (const cb of this.listeners) {
            try { cb(state) } catch {}
        }
    }
}

const hubDiscovery = new HubDiscoveryService()
export default hubDiscovery
